import { useState, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useScrollHighlight } from "@/hooks/use-scroll-highlight";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { useToast } from "@/hooks/use-toast";
import { apiRequest } from "@/lib/queryClient";
import {
  FileText,
  Download,
  Trash2,
  AlertTriangle,
  Plus,
} from "lucide-react";
import { FileViewerDialog, getFileTypeIcon, isPreviewable } from "@/components/ui/file-viewer-dialog";
import type { ViewerFile } from "@/components/ui/file-viewer-dialog";
import { FileUploadDialog } from "@/components/ui/file-upload-dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

interface AnimalFile {
  id: string;
  filename: string;
  displayName: string | null;
  description: string | null;
  mimeType: string | null;
  sizeBytes: number | null;
  uploadedAt: string;
  uploadedBy: string | null;
  sourceContext: string;
  downloadUrl: string | null;
}

interface AnimalFilesResponse {
  files: AnimalFile[];
  total: number;
}

function formatFileSize(bytes: number | null): string {
  if (bytes === null || bytes === undefined) return "Unknown size";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function formatDateTimeET(dateStr: string): string {
  const date = new Date(dateStr);
  if (isNaN(date.getTime())) return dateStr;
  return date.toLocaleString('en-US', {
    timeZone: 'America/New_York',
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  }) + ' ET';
}

export function AnimalFilesTab({ animalId, highlightFileId }: { animalId: string; highlightFileId?: string | null }) {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [untagFileId, setUntagFileId] = useState<string | null>(null);
  const [untagFileName, setUntagFileName] = useState<string>("");
  const [viewerOpen, setViewerOpen] = useState(false);
  const [viewerIndex, setViewerIndex] = useState(0);

  const { data, isLoading, isError, error } = useQuery<AnimalFilesResponse>({
    queryKey: ["/api/animals", animalId, "files"],
  });

  const uploadMutation = useMutation({
    mutationFn: async ({ file, displayName, description }: { file: File; displayName: string; description: string }) => {
      const formData = new FormData();
      formData.append("file", file);
      if (displayName) formData.append("displayName", displayName);
      if (description) formData.append("description", description);
      return apiRequest("POST", `/api/animals/${animalId}/files`, formData);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/animals", animalId, "files"] });
      queryClient.invalidateQueries({ queryKey: ["/api/animals", animalId, "files", "count"] });
      setUploadDialogOpen(false);
      toast({ title: "File uploaded", description: "File has been uploaded and tagged to this animal." });
    },
    onError: (error: Error) => {
      toast({ title: "Upload failed", description: error.message, variant: "destructive" });
    },
  });

  const { isHighlighted, trigger: triggerHighlight } = useScrollHighlight();

  useEffect(() => {
    if (highlightFileId && data && !isLoading) {
      const fileExists = data.files?.some((f: any) => f.fileId === highlightFileId);
      if (fileExists) {
        const timer = setTimeout(() => {
          triggerHighlight(highlightFileId, `file-row-${highlightFileId}`);
        }, 200);
        return () => clearTimeout(timer);
      } else {
        toast({ title: "File not found", description: "This file may have been deleted.", variant: "destructive" });
      }
    }
  }, [highlightFileId, data, isLoading]);

  const untagMutation = useMutation({
    mutationFn: async (fileId: string) => {
      return apiRequest("DELETE", `/api/animals/${animalId}/files/${fileId}/tag`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/animals", animalId, "files"] });
      queryClient.invalidateQueries({ queryKey: ["/api/animals", animalId, "files", "count"] });
      toast({ title: "Tag removed", description: "File has been untagged from this animal." });
      setUntagFileId(null);
    },
    onError: (error: Error) => {
      toast({ title: "Failed to remove tag", description: error.message, variant: "destructive" });
      setUntagFileId(null);
    },
  });

  const handleDownload = (file: AnimalFile) => {
    if (file.downloadUrl) {
      window.open(file.downloadUrl, "_blank");
    }
  };

  const handleOpenViewer = (file: AnimalFile, index: number) => {
    const previewableFiles = filesList.filter((f) => f.downloadUrl && f.mimeType && isPreviewable(f.mimeType));
    if (!file.downloadUrl || !file.mimeType || !isPreviewable(file.mimeType)) {
      handleDownload(file);
      return;
    }
    const vFiles: ViewerFile[] = previewableFiles.map((f) => ({
      url: f.downloadUrl!,
      mimeType: f.mimeType!,
      filename: f.displayName || f.filename,
      description: f.description,
    }));
    const previewableIndex = previewableFiles.indexOf(file);
    setViewerIndex(previewableIndex >= 0 ? previewableIndex : 0);
    setViewerOpen(true);
    setViewerFiles(vFiles);
  };

  const [viewerFiles, setViewerFiles] = useState<ViewerFile[]>([]);

  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Files</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <Skeleton className="h-12 w-full" />
          <Skeleton className="h-12 w-full" />
          <Skeleton className="h-12 w-full" />
        </CardContent>
      </Card>
    );
  }

  if (isError) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Files</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-center text-muted-foreground py-8">
            <AlertTriangle className="h-12 w-12 mx-auto mb-4 opacity-50 text-destructive" />
            <p className="text-destructive">Failed to load files</p>
            <p className="text-xs mt-1">{error?.message || "An error occurred"}</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  const filesList = data?.files ?? [];

  return (
    <>
      <Card>
        <CardHeader className="flex flex-row items-center justify-between gap-2">
          <div>
            <CardTitle>Files</CardTitle>
            <CardDescription>
              {filesList.length === 0
                ? "No files tagged to this animal"
                : `${filesList.length} file${filesList.length !== 1 ? "s" : ""} tagged to this animal`}
            </CardDescription>
          </div>
          <Button
            variant="outline"
            size="icon"
            onClick={() => setUploadDialogOpen(true)}
            data-testid="button-upload-animal-file"
          >
            <Plus className="h-4 w-4" />
          </Button>
        </CardHeader>
        <CardContent>
          {filesList.length === 0 ? (
            <div className="text-center text-muted-foreground py-8">
              <FileText className="h-12 w-12 mx-auto mb-4 opacity-50" />
              <p>No files attached</p>
              <p className="text-xs mt-1">Upload a file or tag files from tasks and meetings</p>
            </div>
          ) : (
            <div className="space-y-2">
              {filesList.map((file, fileIndex) => {
                const IconComponent = getFileTypeIcon(file.mimeType);
                const canPreview = file.downloadUrl && file.mimeType && isPreviewable(file.mimeType);
                return (
                  <div
                    key={file.id}
                    id={`file-row-${file.id}`}
                    className={`flex items-center gap-3 p-3 rounded-md border border-border ${isHighlighted(file.id) ? "ring-2 ring-primary ring-inset bg-primary/5 animate-pulse" : ""}`}
                    data-testid={`file-row-${file.id}`}
                  >
                    <IconComponent className="h-5 w-5 text-muted-foreground flex-shrink-0" />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <button
                          type="button"
                          onClick={() => handleOpenViewer(file, fileIndex)}
                          className={`text-sm font-medium truncate text-left ${canPreview ? "hover:underline cursor-pointer" : ""}`}
                          data-testid={`text-filename-${file.id}`}
                        >
                          {file.displayName || file.filename}
                        </button>
                        <Badge variant="outline" className="text-xs" data-testid={`badge-source-${file.id}`}>
                          {file.sourceContext}
                        </Badge>
                      </div>
                      {file.description && (
                        <p className="text-xs text-muted-foreground mt-0.5 truncate" data-testid={`text-description-${file.id}`}>
                          {file.description}
                        </p>
                      )}
                      <div className="flex items-center gap-2 text-xs text-muted-foreground mt-0.5 flex-wrap">
                        <span>{formatFileSize(file.sizeBytes)}</span>
                        <span>&middot;</span>
                        <span>{formatDateTimeET(file.uploadedAt)}</span>
                        {file.uploadedBy && (
                          <>
                            <span>&middot;</span>
                            <span>{file.uploadedBy}</span>
                          </>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-1 flex-shrink-0">
                      {file.downloadUrl && (
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleDownload(file)}
                          data-testid={`button-download-file-${file.id}`}
                        >
                          <Download className="h-4 w-4" />
                        </Button>
                      )}
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => {
                          setUntagFileId(file.id);
                          setUntagFileName(file.displayName || file.filename);
                        }}
                        data-testid={`button-untag-file-${file.id}`}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      <FileUploadDialog
        open={uploadDialogOpen}
        onOpenChange={setUploadDialogOpen}
        onUpload={(file, displayName, description) => uploadMutation.mutate({ file, displayName, description })}
        isPending={uploadMutation.isPending}
        title="Upload Animal File"
        dialogDescription="Upload a file and tag it to this animal."
      />

      <FileViewerDialog
        open={viewerOpen}
        onOpenChange={setViewerOpen}
        files={viewerFiles}
        initialIndex={viewerIndex}
      />

      <AlertDialog open={!!untagFileId} onOpenChange={(open) => { if (!open) setUntagFileId(null); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remove file tag?</AlertDialogTitle>
            <AlertDialogDescription>
              This will remove "{untagFileName}" from this animal's files. The file itself will not be deleted.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="button-cancel-untag">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => untagFileId && untagMutation.mutate(untagFileId)}
              disabled={untagMutation.isPending}
              data-testid="button-confirm-untag"
            >
              {untagMutation.isPending ? "Removing..." : "Remove Tag"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
